import React from 'react';

import { angleEquals, convertDegreesToRadians } from '@paragon/geometry-library';
import {
  BoundedPlaneShape,
  DisplayablePlaneWithCalculatedGeometry,
  getTotalPlaneElevation,
  isRoofPlane,
} from '@paragon/layout';
import { RaycastOrder } from '@paragon/three-shared';

import { useAppSelector } from '../../../../../components/hooks';
import { is3DLayoutCameraInUse } from '../../../../../store/selectors/layoutModeSelectors';
import { clipLineAroundPoint } from '../../../../util/infiniteLines';
import { useViewCenter } from '../../../../viewCenter';
import { getRoofFaceColor, LayoutPolygonStyle } from '../../../polygon-rendering/common/style';
import { INFINITE_PLANE_VISUAL_LENGTH } from './constants';
import { createVisualDefiningSegment } from './createVisualDefiningSegment';
import { PlaneDefinitionMesh } from './PlaneDefinitionMesh';

interface Props {
  style: LayoutPolygonStyle;

  plane: DisplayablePlaneWithCalculatedGeometry;
  calculatedBounds?: BoundedPlaneShape;
  raycastOrder?: RaycastOrder;
}

export function PlaneDefinitionVisuals({ style, plane, calculatedBounds, raycastOrder }: Props) {
  const threeDimensional = useAppSelector(is3DLayoutCameraInUse);

  const finiteSegments = createVisualDefiningSegment(plane, calculatedBounds);
  const infinite = finiteSegments == null;

  const viewCenter = useViewCenter(infinite);
  const visualBaseSegments = finiteSegments ?? [
    clipLineAroundPoint(plane.calculatedGeometry.line, viewCenter, INFINITE_PLANE_VISUAL_LENGTH),
  ];

  const slope = convertDegreesToRadians(plane.slopeInDegrees);
  const color = getRoofFaceColor(style);

  let elevation = getTotalPlaneElevation(plane);

  // In 2D modes, ensure that roof planes will always be visible above ceiling planes.
  if (isRoofPlane(plane) && !threeDimensional) elevation += 1;

  return visualBaseSegments.map((baseSegment, index) => {
    return (
      <PlaneDefinitionMesh
        key={index}
        color={color}
        length={infinite ? INFINITE_PLANE_VISUAL_LENGTH : baseSegment.length}
        vertical={!threeDimensional && angleEquals(plane.slopeInDegrees, 90)}
        position={[baseSegment.midpoint.x, baseSegment.midpoint.y, elevation]}
        rotation-z={baseSegment.direction.angleInRadians}
        rotation-x={threeDimensional ? slope : 0}
        rotation-order="ZXY"
        userData-raycastOrder={raycastOrder ?? RaycastOrder.Default}
      />
    );
  });
}
